<template>
  <div class="min-h-screen bg-[#f6f7f9]">

    <!-- ── Hero ── -->
    <section class="bg-white border-b border-[#f0f0f0] py-12 lg:py-16">
      <div class="max-w-screen-xl mx-auto px-6">

        <!-- Breadcrumb -->
        <nav class="flex items-center gap-1.5 text-[0.8rem] text-[#999] mb-6 flex-wrap">
          <router-link to="/" class="hover:text-[#111] transition-colors no-underline">
            <i class="bi bi-house"></i>
          </router-link>
          <i class="bi bi-chevron-right text-[0.6rem]"></i>
          <span class="text-[#333] font-medium">Klantenservice</span>
        </nav>

        <div class="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
          <!-- Main heading -->
          <div class="flex-1">
            <span class="inline-flex items-center gap-1.5 text-[0.75rem] font-bold uppercase tracking-wider text-[#7a5f00] bg-[#fffbe6] border border-[#FFD500] px-3 py-1 rounded-full mb-4">
              <i class="bi bi-headset"></i> Klantenservice
            </span>
            <h1 class="text-[clamp(1.75rem,4vw,2.75rem)] font-black text-[#111] leading-tight mb-4">
              Wij staan voor je klaar
            </h1>
            <p class="text-[1.0625rem] text-[#555] leading-relaxed max-w-[560px]">
              Heb je een vraag over je bestelling, levering of product? Ons team helpt je snel en persoonlijk verder, via mail of live chat.
            </p>

            <!-- Quick trust badges -->
            <div class="flex flex-wrap gap-3 mt-6">
              <span class="inline-flex items-center gap-2 bg-[#f0fdf4] border border-green-200 text-green-700 text-[0.82rem] font-semibold px-3.5 py-2 rounded-xl">
                <i class="bi bi-clock-fill"></i> Reactie binnen 1 werkdag
              </span>
              <span class="inline-flex items-center gap-2 bg-[#f0fdf4] border border-green-200 text-green-700 text-[0.82rem] font-semibold px-3.5 py-2 rounded-xl">
                <i class="bi bi-chat-dots-fill"></i> Live chat beschikbaar
              </span>
              <span class="inline-flex items-center gap-2 bg-[#f0fdf4] border border-green-200 text-green-700 text-[0.82rem] font-semibold px-3.5 py-2 rounded-xl">
                <i class="bi bi-lightning-charge-fill"></i> EV-specialisten
              </span>
            </div>
          </div>

          <!-- Sidebar: Openingstijden + Gegevens -->
          <div class="w-full lg:w-72 shrink-0 flex flex-col gap-4">
            <!-- Openingstijden -->
            <div class="bg-white rounded-2xl border border-[#e9eaec] p-6">
              <h3 class="text-[0.8rem] font-black uppercase tracking-widest text-[#999] mb-4">Openingstijden</h3>
              <ul class="flex flex-col gap-2">
                <li v-for="day in openingstijden" :key="day.dag"
                    class="flex justify-between items-center text-[0.875rem]"
                    :class="day.vandaag ? 'font-bold text-[#111]' : 'text-[#555]'">
                  <span class="flex items-center gap-1.5">
                    <span v-if="day.vandaag" class="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0"></span>
                    <span v-else class="w-1.5 h-1.5 rounded-full bg-transparent shrink-0"></span>
                    {{ day.dag }}
                  </span>
                  <span :class="day.gesloten ? 'text-[#bbb]' : ''">{{ day.tijd }}</span>
                </li>
              </ul>
            </div>

            <!-- Bedrijfsgegevens -->
            <div class="bg-white rounded-2xl border border-[#e9eaec] p-6">
              <h3 class="text-[0.8rem] font-black uppercase tracking-widest text-[#999] mb-4">Overige gegevens</h3>
              <ul class="flex flex-col gap-2 text-[0.875rem] text-[#555]">
                <li><span class="text-[#999]">KVK nr.:</span> 76253937</li>
                <li><span class="text-[#999]">BTW nr.:</span> NL003065325B15</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Kennisbank ── -->
    <section class="py-14 lg:py-20">
      <div class="max-w-screen-xl mx-auto px-6">
        <div class="mb-8">
          <h2 class="text-[clamp(1.2rem,2.5vw,1.6rem)] font-black text-[#111] mb-2">Vind het in onze kennisbank</h2>
          <p class="text-[#888] text-[0.9375rem]">Snel antwoord op de meestgestelde vragen</p>
        </div>

        <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <router-link
            v-for="cat in kennisbank"
            :key="cat.label"
            :to="cat.link"
            class="group bg-white rounded-2xl border border-[#e9eaec] p-5 flex items-center gap-4 no-underline hover:border-[#FFD500] hover:shadow-sm transition-all"
          >
            <div class="w-11 h-11 rounded-xl bg-[#fffbe6] flex items-center justify-center shrink-0 group-hover:bg-[#FFD500] transition-colors">
              <i :class="`bi ${cat.icon} text-[#7a5f00] text-[1.15rem] group-hover:text-[#111] transition-colors`"></i>
            </div>
            <div class="flex-1 min-w-0">
              <div class="font-bold text-[#111] text-[0.9375rem] leading-tight">{{ cat.label }}</div>
              <div class="text-[0.8rem] text-[#999] mt-0.5">{{ cat.beschrijving }}</div>
            </div>
            <i class="bi bi-chevron-right text-[#ccc] text-[0.75rem] group-hover:text-[#FFD500] transition-colors shrink-0"></i>
          </router-link>
        </div>
      </div>
    </section>

    <!-- ── Contact ── -->
    <section class="bg-white border-y border-[#f0f0f0] py-14 lg:py-20">
      <div class="max-w-screen-xl mx-auto px-6">
        <div class="grid lg:grid-cols-2 gap-12 lg:gap-20">

          <!-- Contactformulier -->
          <div>
            <h2 class="text-[clamp(1.2rem,2.5vw,1.6rem)] font-black text-[#111] mb-2">Stuur ons een bericht</h2>
            <p class="text-[#888] text-[0.9rem] mb-8">We reageren binnen 1 werkdag op je bericht.</p>

            <form @submit.prevent="verstuur" class="flex flex-col gap-4">
              <div class="grid sm:grid-cols-2 gap-4">
                <div class="flex flex-col gap-1.5">
                  <label class="text-[0.8rem] font-semibold text-[#555]">Naam <span class="text-red-400">*</span></label>
                  <input
                    v-model="form.naam"
                    type="text"
                    placeholder="Jan de Vries"
                    required
                    class="w-full border border-[#e0e0e0] rounded-xl px-4 py-3 text-[0.9rem] text-[#111] placeholder-[#bbb] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-all bg-[#fafafa]"
                  />
                </div>
                <div class="flex flex-col gap-1.5">
                  <label class="text-[0.8rem] font-semibold text-[#555]">E-mailadres <span class="text-red-400">*</span></label>
                  <input
                    v-model="form.email"
                    type="email"
                    placeholder="jan@voorbeeld.nl"
                    required
                    class="w-full border border-[#e0e0e0] rounded-xl px-4 py-3 text-[0.9rem] text-[#111] placeholder-[#bbb] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-all bg-[#fafafa]"
                  />
                </div>
              </div>

              <div class="flex flex-col gap-1.5">
                <label class="text-[0.8rem] font-semibold text-[#555]">Telefoonnummer</label>
                <input
                  v-model="form.telefoon"
                  type="tel"
                  placeholder="06 12 34 56 78"
                  class="w-full border border-[#e0e0e0] rounded-xl px-4 py-3 text-[0.9rem] text-[#111] placeholder-[#bbb] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-all bg-[#fafafa]"
                />
              </div>

              <div class="flex flex-col gap-1.5">
                <label class="text-[0.8rem] font-semibold text-[#555]">Onderwerp <span class="text-red-400">*</span></label>
                <select
                  v-model="form.onderwerp"
                  required
                  class="w-full border border-[#e0e0e0] rounded-xl px-4 py-3 text-[0.9rem] text-[#111] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-all bg-[#fafafa] appearance-none cursor-pointer"
                >
                  <option value="" disabled>Kies een onderwerp...</option>
                  <option value="bestelling">Vraag over mijn bestelling</option>
                  <option value="betaling">Betaling</option>
                  <option value="bezorging">Bezorging & levering</option>
                  <option value="retour">Retourneren</option>
                  <option value="garantie">Garantie</option>
                  <option value="installatie">Installatie</option>
                  <option value="product">Productvraag</option>
                  <option value="overig">Overig</option>
                </select>
              </div>

              <div class="flex flex-col gap-1.5">
                <label class="text-[0.8rem] font-semibold text-[#555]">Jouw vraag of opmerking <span class="text-red-400">*</span></label>
                <textarea
                  v-model="form.bericht"
                  rows="5"
                  placeholder="Typ hier je vraag of opmerking..."
                  required
                  class="w-full border border-[#e0e0e0] rounded-xl px-4 py-3 text-[0.9rem] text-[#111] placeholder-[#bbb] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-all bg-[#fafafa] resize-none"
                ></textarea>
              </div>

              <!-- Succes / fout -->
              <div v-if="status === 'success'" class="flex items-center gap-3 bg-[#f0fdf4] border border-green-200 text-green-700 rounded-xl px-4 py-3 text-[0.875rem] font-medium">
                <i class="bi bi-check-circle-fill text-[1rem]"></i>
                Bedankt! We nemen zo snel mogelijk contact met je op.
              </div>
              <div v-if="status === 'error'" class="flex items-center gap-3 bg-red-50 border border-red-200 text-red-600 rounded-xl px-4 py-3 text-[0.875rem] font-medium">
                <i class="bi bi-exclamation-circle-fill text-[1rem]"></i>
                Er ging iets mis. Probeer het opnieuw of stuur een e-mail.
              </div>

              <button
                type="submit"
                :disabled="bezig"
                class="inline-flex items-center justify-center gap-2 bg-[#FFD500] hover:bg-[#ffb800] disabled:opacity-60 text-[#111] font-bold px-6 py-3.5 rounded-xl transition-all text-[0.9375rem] self-start"
              >
                <i v-if="bezig" class="bi bi-arrow-repeat animate-spin"></i>
                <i v-else class="bi bi-send-fill"></i>
                {{ bezig ? 'Verzenden...' : 'Verstuur bericht' }}
              </button>
            </form>
          </div>

          <!-- Contactgegevens -->
          <div>
            <h2 class="text-[clamp(1.2rem,2.5vw,1.6rem)] font-black text-[#111] mb-2">Contact opnemen</h2>
            <p class="text-[#888] text-[0.9rem] mb-8">Liever direct contact? Wij zijn bereikbaar via onderstaande kanalen.</p>

            <div class="flex flex-col gap-4">
              <!-- E-mail -->
              <a href="mailto:info@plug2charge.nl" class="group flex items-start gap-4 bg-[#f6f7f9] hover:bg-[#fffbe6] border border-[#e9eaec] hover:border-[#FFD500] rounded-2xl p-5 no-underline transition-all">
                <div class="w-11 h-11 rounded-xl bg-white border border-[#e9eaec] flex items-center justify-center shrink-0 group-hover:bg-[#FFD500] group-hover:border-[#FFD500] transition-all">
                  <i class="bi bi-envelope-fill text-[#555] text-[1rem] group-hover:text-[#111] transition-colors"></i>
                </div>
                <div>
                  <div class="font-bold text-[#111] text-[0.9375rem]">info@plug2charge.nl</div>
                  <div class="text-[0.82rem] text-[#888] mt-0.5">Je ontvangt een reactie binnen ongeveer 1 werkdag</div>
                </div>
              </a>

              <!-- Live chat -->
              <a href="/klantenservice#livechat" class="group flex items-start gap-4 bg-[#f6f7f9] hover:bg-[#fffbe6] border border-[#e9eaec] hover:border-[#FFD500] rounded-2xl p-5 no-underline transition-all">
                <div class="w-11 h-11 rounded-xl bg-white border border-[#e9eaec] flex items-center justify-center shrink-0 group-hover:bg-[#FFD500] group-hover:border-[#FFD500] transition-all">
                  <i class="bi bi-chat-dots-fill text-[#555] text-[1rem] group-hover:text-[#111] transition-colors"></i>
                </div>
                <div>
                  <div class="font-bold text-[#111] text-[0.9375rem]">Live chat</div>
                  <div class="text-[0.82rem] text-[#888] mt-0.5">Direct in gesprek met een van onze specialisten</div>
                </div>
              </a>

              <!-- E-mail -->
              <a href="mailto:info@plug2charge.nl" class="group flex items-start gap-4 bg-[#f6f7f9] hover:bg-[#fffbe6] border border-[#e9eaec] hover:border-[#FFD500] rounded-2xl p-5 no-underline transition-all">
                <div class="w-11 h-11 rounded-xl bg-white border border-[#e9eaec] flex items-center justify-center shrink-0 group-hover:bg-[#FFD500] group-hover:border-[#FFD500] transition-all">
                  <i class="bi bi-envelope-fill text-[#555] text-[1rem] group-hover:text-[#111] transition-colors"></i>
                </div>
                <div>
                  <div class="font-bold text-[#111] text-[0.9375rem]">info@plug2charge.nl</div>
                  <div class="text-[0.82rem] text-[#888] mt-0.5">Antwoord binnen 1 werkdag</div>
                </div>
              </a>

              <!-- FAQ -->
              <router-link to="/veelgestelde-vragen" class="group flex items-start gap-4 bg-[#f6f7f9] hover:bg-[#fffbe6] border border-[#e9eaec] hover:border-[#FFD500] rounded-2xl p-5 no-underline transition-all">
                <div class="w-11 h-11 rounded-xl bg-white border border-[#e9eaec] flex items-center justify-center shrink-0 group-hover:bg-[#FFD500] group-hover:border-[#FFD500] transition-all">
                  <i class="bi bi-chat-dots-fill text-[#555] text-[1rem] group-hover:text-[#111] transition-colors"></i>
                </div>
                <div>
                  <div class="font-bold text-[#111] text-[0.9375rem]">Veelgestelde vragen</div>
                  <div class="text-[0.82rem] text-[#888] mt-0.5">Misschien staat jouw vraag al in onze kennisbank</div>
                </div>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Bottom banner ── -->
    <section class="py-14 lg:py-20">
      <div class="max-w-screen-xl mx-auto px-6">
        <div class="bg-gradient-to-r from-[#111] to-[#1a1a1a] rounded-2xl px-6 py-8 flex flex-col sm:flex-row items-center gap-6">
          <div class="w-14 h-14 rounded-2xl bg-[#FFD500]/10 border border-[#FFD500]/20 flex items-center justify-center shrink-0">
            <i class="bi bi-lightning-charge-fill text-[1.75rem] text-[#FFD500]"></i>
          </div>
          <div class="flex-1 text-center sm:text-left">
            <h3 class="text-white font-bold text-[1.05rem] mb-1">EV-specialisten met verstand van zaken</h3>
            <p class="text-[#999] text-[0.85rem]">Ons team bestaat uit specialisten op het gebied van elektrisch rijden en laadoplossingen.</p>
          </div>
          <div class="flex gap-3 shrink-0">
            <a href="/klantenservice#livechat" class="inline-flex items-center gap-2 bg-white hover:bg-[#f0f0f0] text-[#111] font-bold px-4 py-2.5 rounded-xl no-underline transition-all text-[0.875rem]">
              <i class="bi bi-chat-dots"></i> Live chat
            </a>
            <a href="mailto:info@plug2charge.nl" class="inline-flex items-center gap-2 bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold px-4 py-2.5 rounded-xl no-underline transition-all text-[0.875rem]">
              <i class="bi bi-envelope"></i> Mailen
            </a>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

// ── Openingstijden ──
const dagNamen = ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag']
const vandaagIndex = new Date().getDay()

const openingstijden = [
  { dag: 'Maandag',    tijd: '08:00 – 17:00', gesloten: false },
  { dag: 'Dinsdag',    tijd: '08:00 – 17:00', gesloten: false },
  { dag: 'Woensdag',   tijd: '08:00 – 17:00', gesloten: false },
  { dag: 'Donderdag',  tijd: '08:00 – 17:00', gesloten: false },
  { dag: 'Vrijdag',    tijd: '08:00 – 17:00', gesloten: false },
  { dag: 'Zaterdag',   tijd: 'Gesloten',       gesloten: true  },
  { dag: 'Zondag',     tijd: 'Gesloten',       gesloten: true  },
].map(d => ({ ...d, vandaag: d.dag === dagNamen[vandaagIndex] }))

// ── Kennisbank categorieën ──
const kennisbank = [
  { label: 'Bestellen',     beschrijving: 'Hoe plaats ik een bestelling?',       icon: 'bi-cart3',           link: '/veelgestelde-vragen' },
  { label: 'Betalen',       beschrijving: 'Betaalmethoden en facturen',           icon: 'bi-credit-card',     link: '/veelgestelde-vragen' },
  { label: 'Bezorging',     beschrijving: 'Levertijden en bezorgopties',          icon: 'bi-box-seam',        link: '/veelgestelde-vragen' },
  { label: 'Retourneren',   beschrijving: 'Retourbeleid en aanmelden',            icon: 'bi-arrow-return-left', link: '/retourneren' },
  { label: 'Garantie',      beschrijving: 'Garantievoorwaarden en claims',        icon: 'bi-shield-check',    link: '/veelgestelde-vragen' },
  { label: 'Installatie',   beschrijving: 'Installatie en aansluiting',           icon: 'bi-tools',           link: '/installatie' },
  { label: 'EV Laden',      beschrijving: 'Alles over elektrisch laden',          icon: 'bi-lightning-charge', link: '/veelgestelde-vragen' },
  { label: 'Laadpalen',     beschrijving: 'Welke laadpaal past bij jou?',         icon: 'bi-ev-station',      link: '/veelgestelde-vragen' },
  { label: 'Mijn account',  beschrijving: 'Inloggen, bestellingen bekijken',      icon: 'bi-person-circle',   link: '/account' },
]

// ── Contactformulier ──
const form = ref({ naam: '', email: '', telefoon: '', onderwerp: '', bericht: '' })
const bezig = ref(false)
const status = ref(null) // null | 'success' | 'error'

async function verstuur() {
  bezig.value = true
  status.value = null
  try {
    const res = await fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form.value),
    })
    if (!res.ok) throw new Error()
    status.value = 'success'
    form.value = { naam: '', email: '', telefoon: '', onderwerp: '', bericht: '' }
  } catch {
    status.value = 'error'
  } finally {
    bezig.value = false
  }
}
</script>
